/**
 * VatRateSelector Component
 *
 * Presentational VAT rate selector that renders a Select dropdown when
 * `vatRateOptions` are provided, or falls back to a freeform number Input.
 *
 * This component is **data-agnostic** — it does not fetch VAT rates itself.
 * The consuming app is responsible for providing the options.
 *
 * @example
 * ```tsx
 * // Dropdown mode — options provided
 * <VatRateSelector
 *   vatRateOptions={[{ value: 0, label: '0 %' }, { value: 21, label: '21 %' }]}
 *   value={21}
 *   onChange={setVatRate}
 * />
 *
 * // Freeform input mode — no options
 * <VatRateSelector
 *   vatRateOptions={[]}
 *   value={15}
 *   onChange={setVatRate}
 * />
 * ```
 */
import * as React from 'react';
import { Select } from '../selects/Select';
import { Icon } from '../icons/Icon';
import { Input } from './Input';
import type { GlobalSize } from '../../types/global-size';
import { useTranslation } from '../../providers/UIProvider';
import { cn, getTestId, type TestIdProps } from '../../utils';

/**
 * Sentinel option appended to the dropdown that switches the selector into a
 * freeform number input for arbitrary 0–100 rates. Numeric `value` is required
 * by `SelectInputOption`, so we use a value outside the valid VAT range to
 * avoid colliding with real rates.
 */
const CUSTOM_RATE_SENTINEL = -1;

/** Option shape for the VAT rate dropdown. */
export interface VatRateOption {
  value: number;
  label: string;
}

export interface VatRateSelectorProps extends TestIdProps {
  /** Available VAT rate options. When non-empty, renders a Select dropdown. */
  vatRateOptions?: readonly VatRateOption[];
  /** Current VAT rate percentage value */
  value: number | null;
  /** Called when the user selects or types a new VAT rate. `null` means the
   *  user explicitly cleared the freeform input — the caller should treat
   *  this as "unset" (write `null` to the model) rather than as `0`. */
  onChange: (rate: number | null) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /**
   * Height variant — passed through to the underlying Select/Input.
   * @default 'md'
   */
  height?: GlobalSize;
  /** HTML id attribute */
  id?: string;
  /** Accessible name of the control, for a cell whose label is a column header. */
  ariaLabel?: string;
  /** Key handler of the number input; the dropdown keeps its own keys. */
  onInputKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  /** Additional CSS class names */
  className?: string;
}

/**
 * VAT rate selector component.
 *
 * When `vatRateOptions` is provided and non-empty, renders a `Select` dropdown.
 * Otherwise renders a number `Input` (0–100) for freeform entry.
 *
 * The forwarded ref resolves to the underlying interactive element:
 * - **Dropdown mode**: the Headless UI `Listbox` container (`HTMLElement`)
 * - **Freeform mode**: the `<input>` element (`HTMLInputElement`)
 *
 * Both are subtypes of `HTMLElement`, so callers can safely call
 * `ref.current?.focus()` in either mode.
 */
export const VatRateSelector = React.forwardRef<HTMLElement, VatRateSelectorProps>(
  (
    {
      vatRateOptions,
      value,
      onChange,
      disabled = false,
      height = 'md',
      id,
      ariaLabel,
      onInputKeyDown,
      testId,
      className
    },
    ref
  ) => {
    const t = useTranslation('components.vatRateSelector');
    const shouldShowSelect = vatRateOptions && vatRateOptions.length > 0;

    // Active when the user picks "Custom rate…" from the dropdown OR when the
    // current value isn't represented by any predefined option (e.g. an
    // already-saved custom rate that the user opens for editing).
    const valueMatchesPreset = React.useMemo(() => {
      if (!shouldShowSelect || value == null) return false;
      return vatRateOptions!.some(o => o.value === value);
    }, [shouldShowSelect, vatRateOptions, value]);

    const [customMode, setCustomMode] = React.useState(
      shouldShowSelect && value != null && !valueMatchesPreset
    );

    // No auto-revert effect: once the user (or initial off-preset value) puts
    // the component in custom mode it stays there until the explicit back-to-
    // presets button is pressed. Auto-flipping based on `valueMatchesPreset`
    // breaks the moment Custom is picked while the value still equals a preset
    // — the effect would slam customMode back to false before the user could
    // type anything.

    if (shouldShowSelect && !customMode) {
      const optionsWithCustom = [
        ...vatRateOptions!,
        { value: CUSTOM_RATE_SENTINEL, label: t('customRate') }
      ];
      return (
        <Select
          ref={ref as React.Ref<HTMLSelectElement>}
          id={id}
          ariaLabel={ariaLabel}
          testId={getTestId({ testId, id })}
          options={optionsWithCustom}
          value={value}
          onChange={(v: string | number | (string | number)[]) => {
            const next = Number(Array.isArray(v) ? v[0] : v);
            if (next === CUSTOM_RATE_SENTINEL) {
              setCustomMode(true);
              return;
            }
            onChange(next);
          }}
          disabled={disabled}
          height={height}
          className={className}
        />
      );
    }

    // Freeform number input: used when no presets are provided OR when the
    // user explicitly switched into custom mode via the dropdown sentinel.
    const inputEl = (
      <Input
        rightContent={<Icon name='percent' />}
        ref={ref as React.Ref<HTMLInputElement>}
        id={id}
        aria-label={ariaLabel}
        onKeyDown={onInputKeyDown}
        testId={getTestId({ testId, id })}
        type='number'
        min={0}
        max={100}
        step={1}
        autoFocus={customMode}
        value={value ?? ''}
        onChange={e => {
          const parsed = parseFloat(e.target.value);
          if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
            onChange(parsed);
          } else if (e.target.value === '') {
            onChange(null);
          }
        }}
        disabled={disabled}
        height={height}
        className={cn(customMode && 'flex-1 min-w-0', className)}
      />
    );

    if (!customMode) return inputEl;

    // Custom mode: input takes the remaining space, the back-to-presets button
    // sits as a separate sibling on the right so the rotate icon never
    // overlaps the typed value or the (now-hidden) native spinner area. A
    // leading "%" badge labels the rate visually, since the column header gets
    // covered by the freeform input.
    return (
      <div className='flex w-full items-stretch gap-xs no-number-spinners'>
        {inputEl}
        <button
          type='button'
          aria-label={t('backToPresets')}
          title={t('backToPresets')}
          disabled={disabled}
          onClick={() => {
            setCustomMode(false);
            // Snap to the first preset so the Select has something to display.
            const fallback = vatRateOptions?.[0]?.value ?? 0;
            if (!valueMatchesPreset) onChange(fallback);
          }}
          className='shrink-0 inline-flex items-center justify-center w-9 rounded-md border border-border-base bg-background-input text-foreground-muted hover:text-foreground-base hover:bg-background-muted transition-colors disabled:opacity-40 disabled:pointer-events-none'
        >
          <Icon name='arrows-rotate' className='w-3.5 h-3.5' />
        </button>
      </div>
    );
  }
);

VatRateSelector.displayName = 'VatRateSelector';
